import { useCallback, useEffect, useRef, useState } from "react";

const DEFAULT_WATCH_THRESHOLD = 0.9;
const POLL_INTERVAL_MS = 500;

/**
 * Generic video progress tracker.
 * Supports both HTML5 `<video>` elements and YouTube player instances.
 *
 * @param {object} options
 * @param {() => HTMLVideoElement | null} options.getVideoElement - returns the HTML5 video element
 * @param {() => object | null} options.getPlayer - returns a YouTube player instance with getCurrentTime/getDuration
 * @param {() => void} options.onComplete - called once when watch threshold is crossed
 * @param {number} options.threshold - completion fraction (0-1), default 0.9
 */
export const useVideoProgress = ({
  getVideoElement,
  getPlayer,
  onComplete,
  threshold = DEFAULT_WATCH_THRESHOLD,
  contentId,
}) => {
  const [progress, setProgress] = useState(0);
  const completedRef = useRef(false);
  const intervalRef = useRef(null);
  const lastContentIdRef = useRef(contentId);

  const checkComplete = useCallback(
    (currentTime, duration) => {
      if (!duration || completedRef.current) return;

      const percent = currentTime / duration;
      setProgress(percent);

      if (percent >= threshold) {
        completedRef.current = true;
        console.log("[useVideoProgress] completed", { contentId, percent, currentTime, duration });
        onComplete?.();
      }
    },
    [onComplete, threshold, contentId]
  );

  useEffect(() => {
    if (contentId !== lastContentIdRef.current) {
      lastContentIdRef.current = contentId;
      completedRef.current = false;
      setProgress(0);
    }

    intervalRef.current = setInterval(() => {
      if (completedRef.current) return;

      const video = getVideoElement?.();
      const player = getPlayer?.();

      let currentTime;
      let duration;

      if (video) {
        currentTime = video.currentTime;
        duration = video.duration;
      } else if (player) {
        try {
          currentTime = player.getCurrentTime?.();
          duration = player.getDuration?.();
        } catch (e) {
          // Player may not be ready yet.
        }
      }

      if (duration) {
        checkComplete(currentTime, duration);
      }
    }, POLL_INTERVAL_MS);

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [getVideoElement, getPlayer, checkComplete, contentId]);

  return { progress, completed: completedRef.current };
};

export default useVideoProgress;
